import React, { type ChangeEvent } from "react";

import type { HubEntity, SatelliteEntity, TableRecord } from "../../model/types";
import type { DataSource } from "../../../../types/dataVault.types";
import { FormField } from "./FormField";
import { DataSourceEntitySelectors } from "./DataSourceEntitySelectors";
import { SqlEditorField, type SqlAutocompleteContext } from "./SqlEditorField";
import { buildHubOptions } from "../../../../helpers/hubOptions";

export function SatelliteMetadataForm({
  satellite,
  hubs,
  onFieldChange,
  onParentChange,
  dataSources,
  dataSourcesLoading,
  dataSourcesError,
  sqlAutocomplete
}: {
  satellite: SatelliteEntity;
  hubs: HubEntity[];
  onFieldChange: (field: keyof TableRecord, value: string) => void;
  onParentChange: (parentHubId: string | null) => void;
  dataSources: DataSource[];
  dataSourcesLoading: boolean;
  dataSourcesError: string | null;
  sqlAutocomplete?: SqlAutocompleteContext;
}): JSX.Element {
  const isConnectedToHub = Boolean(satellite.parentHubId);
  const inheritedFieldPlaceholder = isConnectedToHub
    ? "Inherited from parent hub"
    : "Select a parent hub to inherit";
  const noop = (_value: string) => {};
  return (
    <section className="tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-white tw-p-6">
      <h3 className="tw-text-sm tw-font-semibold tw-text-slate-800">Satellite metadata</h3>
      <div className="tw-mt-4 tw-grid tw-gap-4">
        <div className="tw-group tw-flex tw-flex-col tw-gap-1.5 tw-border-b tw-border-slate-100 tw-pb-4">
          <label className="tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-500">
            Parent hub
          </label>
          <select
            value={satellite.parentHubId ?? ""}
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              onParentChange(event.target.value || null)
            }
            className="tw-w-full tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-900 focus:tw-border-rose-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-rose-100"
          >
            <option value="">Select a hub</option>
            {buildHubOptions(hubs).map((hub) => (
              <option key={hub.id} value={hub.id}>
                {hub.label}
              </option>
            ))}
          </select>
        </div>
        <FormField
          label="Target table"
          value={satellite.record.target_table}
          onChange={(value) => onFieldChange("target_table", value)}
          placeholder="e.g., sales_item_main_s"
        />
        <FormField
          label="Target schema"
          value={satellite.record.target_schema}
          onChange={noop}
          placeholder={inheritedFieldPlaceholder}
          readOnly
          disabled={!isConnectedToHub}
        />
        <FormField
          label="Subject area"
          value={satellite.record.subject_area}
          onChange={noop}
          placeholder={inheritedFieldPlaceholder}
          readOnly
          disabled={!isConnectedToHub}
        />
        <FormField
          label="Ensemble"
          value={satellite.record.ensemble}
          onChange={noop}
          placeholder={inheritedFieldPlaceholder}
          readOnly
          disabled={!isConnectedToHub}
        />
        <DataSourceEntitySelectors
          dataSources={dataSources}
          loading={dataSourcesLoading}
          error={dataSourcesError}
          selectedDataSource={satellite.record.data_source_name ?? ""}
          selectedSourceEntity={satellite.record.source_entity_name ?? ""}
          onDataSourceChange={(value, dataSource) => {
            onFieldChange("data_source_name", value);
            onFieldChange("data_source_id", dataSource ? String(dataSource.data_source_id) : "");
            onFieldChange("source_entity_name", "");
            onFieldChange("source_entity_id", "");
          }}
          onSourceEntityChange={(value, sourceEntity) => {
            onFieldChange("source_entity_name", value);
            onFieldChange(
              "source_entity_id",
              sourceEntity ? String(sourceEntity.source_entity_id) : ""
            );
          }}
          disabled={!isConnectedToHub}
        />
        <FormField
          label="Description"
          value={satellite.record.Description ?? ""}
          onChange={(value) => onFieldChange("Description", value)}
          multiline
        />
        <SqlEditorField
          label="Source filter"
          value={satellite.record.source_filter ?? ""}
          onChange={(value) => onFieldChange("source_filter", value)}
          placeholder="Enter source filter SQL"
          autocomplete={sqlAutocomplete}
          readOnly={!isConnectedToHub}
          disabled={!isConnectedToHub}
        />
        <SqlEditorField
          label="DQ source filter"
          value={satellite.record.dq_source_filter ?? ""}
          onChange={(value) => onFieldChange("dq_source_filter", value)}
          placeholder="Enter data quality filter SQL"
          autocomplete={sqlAutocomplete}
          readOnly={!isConnectedToHub}
          disabled={!isConnectedToHub}
        />
      </div>
    </section>
  );
}
